import { 
  Accordion, 
  AccordionDetails, 
  AccordionSummary, 
  Button, 
  Checkbox, 
  CircularProgress, 
  Select, 
  styled, 
  TextField, 
  ToggleButton, 
  ToggleButtonGroup, 
  toggleButtonGroupClasses, 
  Tooltip, 
  tooltipClasses, 
  Typography,
  alpha,
  darken
} from "@mui/material";
import { font } from "../../styles/font";

// Add cyan and emerald to palette if not present
const getCyanColor = (theme) => {
  return theme.palette.cyan?.main || theme.palette.info?.main || '#06b6d4';
};

const getEmeraldColor = (theme) => {
  return theme.palette.emerald?.main || theme.palette.success?.main || '#10b981';
};

const getCyanDark = (theme) => {
  return theme.palette.cyan?.dark || darken(getCyanColor(theme), 0.1);
};

const getEmeraldDark = (theme) => {
  return theme.palette.emerald?.dark || darken(getEmeraldColor(theme), 0.1);
};

export const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip 
    {...props} 
    title={
      <Typography 
        fontSize={font.size.fs_5}
        sx={{ color: 'inherit' }}
      >
        {props.title}
      </Typography>
    } 
    disableHoverListener={props.disableHoverListener || !props.title} 
    classes={{ popper: className }} 
  />
))(({ theme }) => {
  return {
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      boxShadow: theme.shadows[4],
      fontSize: font.size.fs_xs,
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${theme.palette.divider}`,
      backdropFilter: theme.palette.mode === 'dark' ? 'blur(10px)' : 'none',
      WebkitBackdropFilter: theme.palette.mode === 'dark' ? 'blur(10px)' : 'none',
      maxWidth: 300,
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.background.paper,
      '&::before': {
        border: `1px solid ${theme.palette.divider}`,
      },
    },
  };
});

const CustomAccordion = styled(Accordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: 'transparent',
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: 0,
  },
}));

export const CustomAccordionComponent = (props) => <CustomAccordion disableGutters elevation={0} {...props} />

const CustomAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  "&:before": {
    display: "none",
  },
  "& .MuiAccordionSummary-content": {
    margin: 0,
  },
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: theme.palette.primary.main,
  },
  "& .MuiAccordionSummary-root.Mui-expanded": {
    minHeight: 0,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  "& .MuiAccordionSummary-root.Mui-expanded .MuiAccordionSummary-content": {
    margin: 0,
  },
  "& .MuiAccordionSummary-root.Mui-expanded .MuiAccordionSummary-expandIconWrapper": {
    color: theme.palette.primary.main,
    transform: 'rotate(180deg)',
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.action.hover, 0.04),
  },
}));

export const CustomAccordionComponentSummary = (props) => <CustomAccordionSummary disableGutters elevation={0} {...props} />

const CustomAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: `0 0 ${theme.shape.borderRadius} ${theme.shape.borderRadius}`,
  borderTop: `1px solid ${theme.palette.divider}`,
  "&:before": {
    display: "none",
  },
}));

export const CustomAccordionComponentDetails = (props) => <CustomAccordionDetails disableGutters elevation={0} {...props} />

const LoadingButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  '&.Mui-disabled': {
    backgroundColor: alpha(theme.palette.action.disabledBackground, 0.5),
    color: theme.palette.action.disabled,
  },
  '& .MuiCircularProgress-root': {
    position: 'absolute',
    left: '50%',
    marginLeft: -8,
  },
}));

export const CustomLoadingButton = (props) => (
  <LoadingButton 
    disableElevation 
    disabled={props?.loading || props.disabled} 
    {...props} 
    startIcon={props?.loading ? null : props.startIcon}
  >
    {props?.loading ? (
      <>
        <CircularProgress size={16} color="inherit" />
        <span style={{ opacity: 0 }}>{props.children}</span>
      </>
    ) : props.children}
  </LoadingButton>
);

const BpIcon = styled('span')(({ theme }) => ({
  borderRadius: 3,
  width: 16,
  height: 16,
  boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.text.primary, 0.2)}`,
  backgroundColor: theme.palette.mode === 'light' ? '#f5f8fa' : '#394b59',
  backgroundImage: theme.palette.mode === 'light' 
    ? 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))'
    : 'linear-gradient(180deg,hsla(0,0%,100%,.05),hsla(0,0%,100%,0))',
  '.Mui-focusVisible &': {
    outline: `2px auto ${alpha(theme.palette.primary.main, 0.6)}`,
    outlineOffset: 2,
  },
  'input:hover ~ &': {
    backgroundColor: theme.palette.mode === 'light' ? '#ebf1f5' : '#30404d',
  },
  'input:disabled ~ &': {
    boxShadow: 'none',
    background: alpha(theme.palette.action.disabled, 0.5),
  },
}));

const BpCheckedIcon = styled(BpIcon)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
  '&::before': {
    display: 'block',
    width: 16,
    height: 16,
    backgroundImage:
      "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath" +
      " fill-rule='evenodd' clip-rule='evenodd' d='M12 5c-.28 0-.53.11-.71.29L7 9.59l-2.29-2.3a1.003 " +
      "1.003 0 00-1.42 1.42l3 3c.18.18.43.29.71.29s.53-.11.71-.29l5-5A1.003 1.003 0 0012 5z' fill='%23fff'/%3E%3C/svg%3E\")",
    content: '""',
  },
  'input:hover ~ &': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

export function CustomCheckbox(props) {
  return (
    <Checkbox
      sx={{ 
        padding: 1,
      }}
      disableRipple
      checkedIcon={<BpCheckedIcon />}
      icon={<BpIcon />}
      inputProps={{ 'aria-label': 'Checkbox demo' }}
      {...props}
    />
  );
}

export const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    margin: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    [`&.${toggleButtonGroupClasses.disabled}`]: {
      border: `1px solid ${alpha(theme.palette.action.disabled, 0.12)}`,
    },
  },
  [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]: {
    marginLeft: -1,
    borderLeft: '1px solid transparent',
  },
}));

export const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: alpha(theme.palette.action.hover, 0.04),
    color: theme.palette.text.primary,
  },
  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
  },
  '&.Mui-disabled': {
    color: theme.palette.action.disabled,
  },
}));

export const StyledGradientButton = styled(Button)(({ theme }) => {
  const cyan = getCyanColor(theme);
  const emerald = getEmeraldColor(theme);
  const cyanDark = getCyanDark(theme);
  const emeraldDark = getEmeraldDark(theme);
  
  return {
    color: theme.palette.getContrastText(cyan),
    backgroundImage: `linear-gradient(90deg, ${cyan} 0%, ${emerald} 100%)`,
    "&:hover": {
      backgroundImage: `linear-gradient(90deg, ${cyanDark} 0%, ${emeraldDark} 100%)`,
    },
    "&.Mui-disabled": {
      backgroundImage: `linear-gradient(90deg, ${alpha(theme.palette.action.disabled, 0.12)} 0%, ${alpha(theme.palette.action.disabled, 0.12)} 100%)`,
      color: theme.palette.action.disabled,
    }
  };
});

export const StyledGradientToggleButton = styled(ToggleButton)(({ theme }) => {
  const cyan = getCyanColor(theme);
  const emerald = getEmeraldColor(theme);
  const cyanDark = getCyanDark(theme);
  const emeraldDark = getEmeraldDark(theme);
  
  return {
    color: theme.palette.getContrastText(cyan),
    backgroundImage: `linear-gradient(90deg, ${cyan} 0%, ${emerald} 100%)`,
    "&:hover": {
      backgroundImage: `linear-gradient(90deg, ${cyanDark} 0%, ${emeraldDark} 100%)`,
    },
    "&.Mui-selected": {
      backgroundImage: `linear-gradient(90deg, ${cyanDark} 0%, ${emeraldDark} 100%)`,
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-disabled": {
      backgroundImage: `linear-gradient(90deg, ${alpha(theme.palette.action.disabled, 0.12)} 0%, ${alpha(theme.palette.action.disabled, 0.12)} 100%)`,
      color: theme.palette.action.disabled,
    }
  };
});

export const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    "& fieldset": {
      borderColor: theme.palette.divider,
      borderRadius: theme.shape.borderRadius,
    },
    "&:hover fieldset": {
      borderColor: alpha(theme.palette.primary.main, 0.5),
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
  "& .MuiInputLabel-root": {
    color: theme.palette.text.secondary,
    "&.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
}));

export const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
      borderRadius: theme.shape.borderRadius,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.primary.main, 0.5),
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
  '& .MuiSelect-select': {
    padding: theme.spacing(1, 2),
    minHeight: 'auto',
  },
  '& .MuiSelect-icon': {
    color: theme.palette.text.secondary,
  },
}));